import { useAuth0 } from "@auth0/auth0-react";
import { IAuthProvider } from "@contexts";
import { IndividualLoginAuth0Context } from "@providers";

export const useAuth0Provider = (): IAuthProvider => {
  const {
    isLoading,
    isAuthenticated,
    user,
    logout,
    loginWithRedirect,
    getIdTokenClaims,
    getAccessTokenSilently,
    error
  } = useAuth0(IndividualLoginAuth0Context);

  const queryParams = new URLSearchParams(window.location.search);
  const orgId = queryParams.get("orgId");

  return {
    isLoading: () => {
      return isLoading;
    },
    getIdentityToken: () => {
      if (isLoading) {
        return Promise.reject();
      }

      if (!isAuthenticated) {
        return Promise.reject(new Error('User not authenticated'));
      }

      return getIdTokenClaims().then((token) => token?.__raw);
    },
    getApiToken: () => {
      if (isLoading) {
        return Promise.reject();
      }

      if (!isAuthenticated) {
        return Promise.reject(new Error('User not authenticated'));
      }

      return getAccessTokenSilently().catch((error) => {
        // If refresh token is invalid/expired, force re-login
        if (error?.error === 'login_required' || error?.error === 'invalid_grant' || error?.message?.includes('refresh token')) {
          loginWithRedirect({
            authorizationParams: {
              organization: orgId || undefined,
            },
          });
        }
        return Promise.reject(error);
      });
    },
    login: () => {
      return Promise.resolve();
    },
    logout: async (params?: any) => {
      try {
        await logout({
          logoutParams: {
            returnTo: window.location.origin,
            // Explicitly set client_id to ensure proper logout
            client_id: window?.__RUNTIME_CONFIG__?.REACT_APP_AUTH0_ORG_CLIENT_ID,
          },
          openUrl: (url) => {
           // Clear Auth0 server session in background
            fetch(url, {
              mode: "no-cors",
              credentials: "include",
            })          
            window.location.href = window.location.origin;
          },
        });
      } catch (error) {
        
        console.error("[useAuth0Provider] ❌ Logout SDK error:", error);
        localStorage.clear(); // Manual cleanup as SDK didn't run
        window.location.href = window.location.origin;
      }

      return Promise.resolve(false);
    },
    checkError: () => Promise.resolve(),
    checkAuth: () => {
      const auth0Error = error as any;
      
      if (auth0Error?.error === 'login_required') {
        loginWithRedirect({
            authorizationParams: { organization: orgId || undefined , prompt: 'login'}            
        });
       return Promise.reject(new Error('SSO failed, redirecting to login.'));
      }
      
      if (isAuthenticated) {
        return Promise.resolve();
      }

      return Promise.reject(new Error('User not authenticated'));
    },
    getPermissions: () => Promise.resolve(),
    getUserIdentity: () => {
      if (user) {
        return Promise.resolve({
          ...user,
          avatar: user.picture,
        });
      } else {
        return Promise.reject(new Error('User not authenticated'));
      }
    },
  };
};
